import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowsClockwise, Paperclip } from "@phosphor-icons/react";

const KINDS = {
  invoice_send: { label: "Sent", color: "bg-green-100 text-green-800" },
  invoice_reminder: { label: "Reminder", color: "bg-blue-100 text-blue-800" },
  client_reminder: { label: "Client reminder", color: "bg-rose-100 text-rose-800" },
  statement: { label: "Statement", color: "bg-emerald-100 text-emerald-800" },
  year_end_1099: { label: "1099 summary", color: "bg-violet-100 text-violet-800" },
  tax_info_request: { label: "Tax info request", color: "bg-amber-100 text-amber-800" },
  invoice_auto_nudge: { label: "Auto nudge (retired)", color: "bg-slate-100 text-slate-600" },
};

const STATUS = {
  sent: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-800",
};

const DELIVERY = {
  delivered: "bg-emerald-100 text-emerald-800",
  bounced: "bg-red-100 text-red-800",
  complained: "bg-red-100 text-red-800",
  failed: "bg-red-100 text-red-800",
  delivery_delayed: "bg-amber-100 text-amber-800",
};

export default function SentEmails() {
  const [rows, setRows] = useState([]);
  const [kind, setKind] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const q = kind ? `?kind=${kind}` : "";
      const r = await api.get(`/sent-emails${q}`);
      setRows(Array.isArray(r.data) ? r.data : []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const totals = useMemo(() => ({
    total: rows.length,
    sent: rows.filter((r) => r.status === "sent").length,
    failed: rows.filter((r) => r.status === "failed").length,
    bounced: rows.filter((r) => ["bounced", "complained", "failed"].includes(r.delivery_status)).length,
  }), [rows]);

  const fmt = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="p-4 md:p-8">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="tinylabel">Audit</div>
          <h1 className="text-3xl font-black">Sent Emails</h1>
          <p className="text-slate-500 mt-1 text-sm">Every invoice, reminder, statement and tax-info email that has left the app. Client reminders are always manual — nothing is sent automatically.</p>
        </div>
        <button data-testid="refresh-sent-emails" onClick={load}
          className="h-11 px-4 border border-slate-300 rounded-md font-semibold hover:bg-slate-50 flex items-center gap-2">
          <ArrowsClockwise size={16} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-4 gap-4 mb-6">
        {[
          { label: "Total shown", value: totals.total, tone: "text-slate-900" },
          { label: "Sent", value: totals.sent, tone: "text-green-600" },
          { label: "Failed", value: totals.failed, tone: "text-red-600" },
          { label: "Bounced", value: totals.bounced, tone: totals.bounced > 0 ? "text-red-600" : "text-slate-400" },
        ].map((s) => (
          <div key={s.label} className="bg-white border border-slate-200 rounded-md p-4">
            <div className="tinylabel">{s.label}</div>
            <div className={`text-2xl font-black font-mono mt-1 ${s.tone}`}>{s.value}</div>
          </div>
        ))}
      </div>

      <div className="mb-4 flex gap-2 items-center">
        <label className="tinylabel">Filter kind</label>
        <select data-testid="sent-email-kind-filter" value={kind} onChange={(e) => setKind(e.target.value)}
          className="h-10 px-3 border border-slate-300 rounded-md bg-white">
          <option value="">All</option>
          <option value="invoice_send">Initial send</option>
          <option value="invoice_reminder">Reminder (per invoice)</option>
          <option value="client_reminder">Client reminder (90+ summary)</option>
          <option value="statement">Statement</option>
          <option value="year_end_1099">1099 summary</option>
          <option value="tax_info_request">Tax info request</option>
          <option value="invoice_auto_nudge">Auto-nudge (retired — historical)</option>
        </select>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">When (UTC)</th>
              <th className="text-left px-4 py-3">Kind</th>
              <th className="text-left px-4 py-3">Invoice</th>
              <th className="text-left px-4 py-3">To</th>
              <th className="text-left px-4 py-3">BCC</th>
              <th className="text-center px-4 py-3">Att.</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Actor</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td className="px-4 py-6 text-slate-400" colSpan={8}>Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td className="px-4 py-6 text-slate-400" colSpan={8}>No sent emails yet. Send an invoice to start populating this log.</td></tr>}
            {!loading && rows.map((r) => {
              const k = KINDS[r.kind] || { label: r.kind, color: "bg-slate-100 text-slate-700" };
              const st = STATUS[r.status] || "bg-slate-100 text-slate-700";
              return (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50 align-top" data-testid={`sent-email-row-${r.id}`}>
                  <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{fmt(r.created_at)}</td>
                  <td className="px-4 py-3"><span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${k.color}`}>{k.label}</span></td>
                  <td className="px-4 py-3">
                    {r.invoice_id ? (
                      <Link data-testid={`open-invoice-${r.invoice_id}`} to={`/invoices/${r.invoice_id}`} className="text-green-600 font-semibold font-mono">{r.invoice_number || r.invoice_id.slice(0, 8)}</Link>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-600 break-all">{r.to}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs break-all">{(r.bcc || []).join(", ") || "—"}</td>
                  <td className="px-4 py-3 text-center">
                    {r.attachment_count > 0 ? (
                      <span className="inline-flex items-center gap-1 text-slate-600 font-mono">
                        <Paperclip size={13} /> {r.attachment_count}
                      </span>
                    ) : <span className="text-slate-300">0</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold uppercase ${st}`}>{r.status}</span>
                    {r.delivery_status && DELIVERY[r.delivery_status] && (
                      <span className={`ml-1 inline-block px-2 py-0.5 rounded text-xs font-semibold uppercase ${DELIVERY[r.delivery_status]}`}
                            data-testid={`delivery-badge-${r.id}`}>{r.delivery_status}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{r.actor}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
